export interface WhatsAppAccessUser {
  role?: string | null;
  company?: {
    modules?: {
      whatsapp?: boolean;
      whatsappAi?: boolean;
      [key: string]: unknown;
    };
  } | null;
}

export type WhatsAppSection = 'atendimento' | 'config' | 'gestao' | 'ia' | 'ai_reviews' | 'ai_legacy' | 'admin';

export interface WhatsAppSectionItem {
  id: WhatsAppSection;
  label: string;
  path: string;
}

function normalizeRole(role?: string | null) {
  return String(role || '').trim().toLowerCase();
}

export function hasWhatsAppModule(user: WhatsAppAccessUser | null | undefined) {
  const role = normalizeRole(user?.role);
  if (role === 'superadmin') return true;
  return user?.company?.modules?.whatsapp === true;
}

export function canAccessWhatsAppAtendimento(user: WhatsAppAccessUser | null | undefined) {
  return hasWhatsAppModule(user);
}

export function canAccessWhatsAppGestao(user: WhatsAppAccessUser | null | undefined) {
  if (!hasWhatsAppModule(user)) return false;
  const role = normalizeRole(user?.role);
  return ['supervisor', 'administrador', 'admin', 'superadmin'].includes(role);
}

export function canAccessWhatsAppAdmin(user: WhatsAppAccessUser | null | undefined) {
  if (!hasWhatsAppModule(user)) return false;
  const role = normalizeRole(user?.role);
  return ['administrador', 'admin', 'superadmin'].includes(role);
}

export function hasWhatsAppAiModule(user: WhatsAppAccessUser | null | undefined) {
  const role = normalizeRole(user?.role);
  if (role === 'superadmin') return true;
  return hasWhatsAppModule(user) && user?.company?.modules?.whatsappAi === true;
}

export function canAccessWhatsAppAi(user: WhatsAppAccessUser | null | undefined) {
  if (!hasWhatsAppAiModule(user)) return false;
  return canAccessWhatsAppGestao(user);
}

export function canAccessWhatsAppAiLegacy(user: WhatsAppAccessUser | null | undefined) {
  if (!hasWhatsAppAiModule(user)) return false;
  const role = normalizeRole(user?.role);
  return role === 'superadmin';
}

export function getWhatsAppSections(user: WhatsAppAccessUser | null | undefined): WhatsAppSectionItem[] {
  const sections: WhatsAppSectionItem[] = [];

  if (canAccessWhatsAppAtendimento(user)) {
    sections.push({ id: 'atendimento', label: 'Atendimento', path: '/chat/atendimento' });
  }
  if (canAccessWhatsAppGestao(user)) {
    sections.push({ id: 'config', label: 'Visão geral', path: '/chat/config' });
    sections.push({ id: 'gestao', label: 'Atendimento', path: '/chat/gestao' });
  }
  if (canAccessWhatsAppAi(user)) {
    sections.push({ id: 'ia', label: 'Autonomia IA', path: '/chat/ia' });
    sections.push({ id: 'ai_reviews', label: 'Qualidade IA', path: '/chat/ia/reviews' });
  }
  if (canAccessWhatsAppAiLegacy(user)) {
    sections.push({ id: 'ai_legacy', label: 'Base Legado', path: '/chat/ia/legacy' });
  }
  if (canAccessWhatsAppAdmin(user)) {
    sections.push({ id: 'admin', label: 'Administração', path: '/chat/admin' });
  }

  return sections;
}

export function getDefaultWhatsAppPath(user: WhatsAppAccessUser | null | undefined) {
  return getWhatsAppSections(user)[0]?.path || '/dashboard';
}

export function getPreferredWhatsAppSettingsPath(user: WhatsAppAccessUser | null | undefined) {
  if (canAccessWhatsAppGestao(user)) return '/chat/config';
  if (canAccessWhatsAppAi(user)) return '/chat/ia';
  if (canAccessWhatsAppAdmin(user)) return '/chat/admin';
  return '/chat/atendimento';
}
